class TestimonialMarquee {
  constructor(sectionId) {
    this.section = document.getElementById(`video-testimonials-${sectionId}`);
    if (!this.section || this.section.dataset.marqueeReady === "true") return;

    this.container = this.section.querySelector(".carousel-container");
    this.grid = this.section.querySelector(".videos-grid");
    this.navPrev = this.section.querySelector(".nav-button.prev");
    this.navNext = this.section.querySelector(".nav-button.next");
    this.realCards = Array.from(this.grid?.children ?? []);
    if (!this.container || !this.grid || this.realCards.length === 0) return;

    this.section.dataset.marqueeReady = "true";
    this.position = 0;
    this.loopWidth = 0;
    this.cardPitch = 0;
    this.speedCardsPerSecond = 0.96;
    this.isDragging = false;
    this.didDrag = false;
    this.resumeAt = 0;
    this.lastFrame = null;
    this.frameId = null;
    this.resizeTimer = null;
    this.reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    this.isNearViewport = false;

    this.appendClones();
    this.bindControls();
    this.bindPointerDrag();
    this.bindViewportObserver();
    this.measure();
    this.render();
  }

  appendClones() {
    this.realCards.forEach((card, index) => {
      card.dataset.reviewIndex = String(index + 1);
      const clone = card.cloneNode(true);
      clone.dataset.reviewClone = "true";
      clone.setAttribute("aria-hidden", "true");
      clone.removeAttribute("data-shopify-editor-block");
      clone.querySelectorAll("[id]").forEach((element) => element.removeAttribute("id"));
      this.grid.appendChild(clone);
    });
  }

  normalize(value) {
    if (!this.loopWidth) return 0;
    return ((value % this.loopWidth) + this.loopWidth) % this.loopWidth;
  }

  measure() {
    const firstCard = this.realCards[0];
    const secondCard = this.realCards[1];
    const firstClone = this.grid.querySelector('[data-review-clone="true"]');
    if (!firstCard || !firstClone) return;

    this.loopWidth = firstClone.offsetLeft - firstCard.offsetLeft;
    this.cardPitch = secondCard
      ? secondCard.offsetLeft - firstCard.offsetLeft
      : firstCard.getBoundingClientRect().width;
    this.position = this.normalize(this.position);
  }

  render() {
    this.grid.style.transform = `translate3d(${-this.position}px, 0, 0)`;
  }

  shouldAnimate() {
    return !this.reducedMotion && this.isNearViewport && !document.hidden;
  }

  start() {
    if (!this.shouldAnimate() || this.frameId !== null) return;
    this.grid.classList.add("is-marquee-active");
    this.frameId = requestAnimationFrame((time) => this.tick(time));
  }

  stop() {
    if (this.frameId !== null) cancelAnimationFrame(this.frameId);
    this.frameId = null;
    this.lastFrame = null;
    this.grid.classList.remove("is-marquee-active");
  }

  tick(time) {
    this.frameId = null;
    if (!this.shouldAnimate()) return;

    if (this.lastFrame === null) this.lastFrame = time;
    const elapsed = Math.min(time - this.lastFrame, 50);
    this.lastFrame = time;

    if (!this.isDragging && time >= this.resumeAt) {
      const pixelsPerSecond = this.cardPitch * this.speedCardsPerSecond;
      this.position = this.normalize(this.position + (pixelsPerSecond * elapsed) / 1000);
      this.render();
    }

    this.start();
  }

  pauseBriefly(duration = 900) {
    this.resumeAt = performance.now() + duration;
  }

  moveByCards(cardCount) {
    this.position = this.normalize(this.position + this.cardPitch * cardCount);
    this.render();
    this.pauseBriefly();
  }

  bindControls() {
    this.navPrev?.addEventListener("click", () => this.moveByCards(-1));
    this.navNext?.addEventListener("click", () => this.moveByCards(1));

    window.addEventListener("resize", () => {
      clearTimeout(this.resizeTimer);
      this.resizeTimer = setTimeout(() => {
        const progress = this.loopWidth ? this.position / this.loopWidth : 0;
        this.measure();
        this.position = this.loopWidth * progress;
        this.render();
      }, 120);
    });
  }

  bindViewportObserver() {
    const updateAnimationState = () => {
      if (document.hidden) {
        this.stop();
      } else {
        this.start();
      }
    };

    document.addEventListener("visibilitychange", updateAnimationState);

    if (!("IntersectionObserver" in window)) {
      this.isNearViewport = true;
      this.start();
      return;
    }

    this.viewportObserver = new IntersectionObserver(
      ([entry]) => {
        this.isNearViewport = entry.isIntersecting;
        if (this.isNearViewport) {
          this.start();
        } else {
          this.stop();
        }
      },
      { rootMargin: "250px 0px" },
    );
    this.viewportObserver.observe(this.section);
  }

  bindPointerDrag() {
    let pointerId = null;
    let startX = 0;
    let startPosition = 0;

    const finishDrag = (event) => {
      if (!this.isDragging || event.pointerId !== pointerId) return;
      this.isDragging = false;
      this.container.classList.remove("is-dragging");
      if (this.container.hasPointerCapture(pointerId)) this.container.releasePointerCapture(pointerId);
      pointerId = null;
      this.pauseBriefly(700);
    };

    this.container.addEventListener("pointerdown", (event) => {
      if (event.pointerType === "mouse" && event.button !== 0) return;
      if (event.target.closest("button, a")) return;
      pointerId = event.pointerId;
      startX = event.clientX;
      startPosition = this.position;
      this.isDragging = true;
      this.didDrag = false;
      this.container.classList.add("is-dragging");
      this.container.setPointerCapture(pointerId);
    });

    this.container.addEventListener("pointermove", (event) => {
      if (!this.isDragging || event.pointerId !== pointerId) return;
      const distance = event.clientX - startX;
      if (Math.abs(distance) > 4) this.didDrag = true;
      this.position = this.normalize(startPosition - distance);
      this.render();
    });

    this.container.addEventListener("pointerup", finishDrag);
    this.container.addEventListener("pointercancel", finishDrag);
    this.container.addEventListener(
      "click",
      (event) => {
        if (!this.didDrag) return;
        event.preventDefault();
        event.stopPropagation();
        this.didDrag = false;
      },
      true,
    );
  }

}

function initVideoTestimonials(sectionId) {
  new TestimonialMarquee(sectionId);
}

window.initVideoTestimonials = initVideoTestimonials;
